/**
 * useUsageRollupDashboard (issue #1453, epic #1448)
 *
 * Drives the Analytics page's usage-rollup widget: DAU/WAU/MAU, top routes/
 * features, query volume by entity type, and session duration/depth/
 * drop-off. Fetches on mount; failures from any of the four calls surface
 * as a single `error` rather than partially rendering (the widget's
 * sections all come from the same usage-event pipeline, so a failure here
 * usually means the whole endpoint is down, not one metric).
 *
 * Each of the three `fetchUsageRollup` calls (day/week/month) queries the
 * same `LOOKBACK_DAYS`-wide window — top routes/features/query-volume are
 * meant to summarize that whole window, not just the latest bucket. DAU/WAU/
 * MAU, however, must reflect the *current* day/week/month specifically: this
 * used to grab the series' last bucket, which silently surfaced a stale
 * prior period's count (with no indication in the UI) whenever nothing had
 * happened yet in the current one. `currentPeriodValue` instead looks up the
 * bucket matching *now*, so an inactive current period correctly reads 0
 * rather than pulling a number forward from days ago.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchUsageRollup,
  fetchSessionBehavior,
  type UsageRollupResult,
  type UsageRollupGranularity,
  type SessionBehaviorResult,
} from '../lib/apiClient/usageAnalytics';

export interface UsageRollupDashboardData {
  dailyActiveUsers: number;
  weeklyActiveUsers: number;
  monthlyActiveUsers: number;
  topRoutes: UsageRollupResult['topRoutes'];
  topFeatures: UsageRollupResult['topFeatures'];
  queryVolumeByEntityType: UsageRollupResult['queryVolumeByEntityType'];
  sessionBehavior: SessionBehaviorResult;
}

export interface UseUsageRollupDashboardResult {
  data: UsageRollupDashboardData | null;
  loading: boolean;
  error: string | null;
}

/** Wide enough that a week/month bucket is virtually guaranteed to exist even right after a quiet period, without being an unreasonably large query window. */
const LOOKBACK_DAYS = 90;

/**
 * Bucket key for `now` under a given granularity — mirrors
 * `backend/src/repositories/MetricsRepository.ts` `bucketTimestamp()`
 * exactly (UTC day / UTC-Sunday-start week / UTC month). Must stay in sync
 * with that function, same as `KNOWN_FEATURE_EVENT_TYPES` staying in sync
 * with `usageTracking.ts` call sites.
 */
function currentBucketKey(now: Date, granularity: UsageRollupGranularity): string {
  switch (granularity) {
    case 'day':
      return now.toISOString().split('T')[0] + 'T00:00:00.000Z';
    case 'week': {
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - d.getUTCDay());
      return d.toISOString().split('T')[0] + 'T00:00:00.000Z';
    }
    case 'month':
      return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01T00:00:00.000Z`;
  }
}

/**
 * The active-users count for the bucket matching *now*, or 0 when that
 * bucket isn't in the series (no activity yet in the current day/week/
 * month). Deliberately does NOT fall back to the series' last bucket —
 * that silently reported a stale prior period's count with no indication
 * in the UI (flagged in PR #1575 review).
 */
function currentPeriodValue(
  series: UsageRollupResult['activeUsers'],
  granularity: UsageRollupGranularity,
  now: Date
): number {
  const key = currentBucketKey(now, granularity);
  return series.find((point) => point.timestamp === key)?.value ?? 0;
}

export function useUsageRollupDashboard(): UseUsageRollupDashboardResult {
  const [data, setData] = useState<UsageRollupDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const end = now.toISOString();
      const start = new Date(now.getTime() - LOOKBACK_DAYS * 24 * 60 * 60 * 1000).toISOString();

      const [dayRollup, weekRollup, monthRollup, sessionBehavior] = await Promise.all([
        fetchUsageRollup({ granularity: 'day', start, end }),
        fetchUsageRollup({ granularity: 'week', start, end }),
        fetchUsageRollup({ granularity: 'month', start, end }),
        fetchSessionBehavior({ start, end }),
      ]);

      setData({
        dailyActiveUsers: currentPeriodValue(dayRollup.activeUsers, 'day', now),
        weeklyActiveUsers: currentPeriodValue(weekRollup.activeUsers, 'week', now),
        monthlyActiveUsers: currentPeriodValue(monthRollup.activeUsers, 'month', now),
        topRoutes: dayRollup.topRoutes,
        topFeatures: dayRollup.topFeatures,
        queryVolumeByEntityType: dayRollup.queryVolumeByEntityType,
        sessionBehavior,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage rollup data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error };
}
